/**
 * L4 — Birgit-extensible OCR rules editor.
 *
 * Two sections:
 *  1. Marginalia tokens — silent-drop list. Add/deactivate without dev help.
 *  2. Top correction patterns — view what users keep correcting; manual
 *     promotion to rules.jsonl via the existing rules.rebuild trigger.
 */
import { useEffect, useState } from "react";

interface MarginaliaToken {
  id: string;
  token: string;
  added_by: string | null;
  added_at: string;
  active: boolean;
  comment: string | null;
}

interface CorrectionPattern {
  scope: string;
  match_value: string;
  replace_value: string;
  occurrence_count: number;
  last_seen: string;
}

const API = "/api/admin";

export function SettingsOcrRules() {
  const [tokens, setTokens] = useState<MarginaliaToken[]>([]);
  const [patterns, setPatterns] = useState<CorrectionPattern[]>([]);
  const [newToken, setNewToken] = useState("");
  const [newComment, setNewComment] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  const loadAll = async () => {
    setErr(null);
    try {
      const [t, p] = await Promise.all([
        fetch(`${API}/marginalia`, { credentials: "same-origin" }).then((r) => r.json()),
        fetch(`${API}/correction-patterns?limit=30`, { credentials: "same-origin" }).then((r) => r.json()),
      ]);
      setTokens(t.tokens ?? []);
      setPatterns(p.patterns ?? []);
    } catch (e: any) {
      setErr(e.message);
    }
  };

  useEffect(() => { loadAll(); }, []);

  const addToken = async () => {
    if (!newToken.trim()) return;
    setBusy(true);
    setErr(null);
    setInfo(null);
    try {
      const r = await fetch(`${API}/marginalia`, {
        method: "POST",
        credentials: "same-origin",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ token: newToken.trim(), comment: newComment.trim() || undefined }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setNewToken("");
      setNewComment("");
      setInfo(`Token "${newToken}" hinzugefügt — gilt ab dem nächsten OCR-Lauf.`);
      await loadAll();
    } catch (e: any) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };

  const deactivateToken = async (id: string, label: string) => {
    if (!confirm(`Token "${label}" deaktivieren?`)) return;
    setBusy(true);
    try {
      await fetch(`${API}/marginalia/${id}`, {
        method: "DELETE",
        credentials: "same-origin",
      });
      setInfo(`Token "${label}" deaktiviert.`);
      await loadAll();
    } catch (e: any) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };

  const promotePatterns = async () => {
    if (!confirm("Alle Korrektur-Patterns mit Häufigkeit ≥3 in rules.jsonl übernehmen?")) return;
    setBusy(true);
    try {
      const r = await fetch(`${API}/rules/rebuild`, {
        method: "POST",
        credentials: "same-origin",
      });
      const body = await r.json();
      setInfo(`Rules-Rebuild gestartet: ${body.status ?? "ok"}.`);
    } catch (e: any) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="settings-ocr-rules" style={{ display: "flex", flexDirection: "column", gap: 32 }}>
      {err && <div className="error-box">{err}</div>}
      {info && <div className="info-box" style={{ padding: 12, background: "#e6f7e6", border: "1px solid #b6e6b6", borderRadius: 4 }}>{info}</div>}

      <section>
        <h3>Marginalia-Tokens (klasse 5a — werden still ignoriert)</h3>
        <p className="muted" style={{ maxWidth: 720 }}>
          Wenn die OCR ein Token aus dieser Liste in einer Zelle liest, wird die Zelle leer zurückgegeben (keine Warnmeldung in der UI).
          Verwende das hier für offensichtliche OCR-Artefakte wie "TAG", "Mo", "Di", die als Zellinhalt keinen Sinn ergeben.
        </p>

        <div style={{ display: "flex", gap: 8, alignItems: "flex-end", margin: "16px 0" }}>
          <label>
            Neues Token
            <input
              type="text"
              value={newToken}
              onChange={(e) => setNewToken(e.target.value)}
              placeholder="z.B. TAG"
              disabled={busy}
              style={{ display: "block", marginTop: 4 }}
            />
          </label>
          <label style={{ flex: 1 }}>
            Kommentar (optional)
            <input
              type="text"
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              placeholder="Warum sollte das ignoriert werden?"
              disabled={busy}
              style={{ display: "block", marginTop: 4, width: "100%" }}
            />
          </label>
          <button type="button" className="primary" onClick={addToken} disabled={busy || !newToken.trim()}>
            Hinzufügen
          </button>
        </div>

        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ borderBottom: "1px solid #ccc" }}>
              <th style={{ textAlign: "left", padding: 4 }}>Token</th>
              <th style={{ textAlign: "left", padding: 4 }}>Status</th>
              <th style={{ textAlign: "left", padding: 4 }}>Kommentar</th>
              <th style={{ textAlign: "left", padding: 4 }}>Hinzugefügt</th>
              <th style={{ textAlign: "left", padding: 4 }}></th>
            </tr>
          </thead>
          <tbody>
            {tokens.map((t) => (
              <tr key={t.id} style={{ borderBottom: "1px solid #eee", opacity: t.active ? 1 : 0.5 }}>
                <td style={{ padding: 4, fontFamily: "monospace" }}>{t.token}</td>
                <td style={{ padding: 4 }}>{t.active ? "aktiv" : "deaktiviert"}</td>
                <td style={{ padding: 4 }}>{t.comment ?? <span className="muted">—</span>}</td>
                <td style={{ padding: 4 }}>{new Date(t.added_at).toLocaleDateString("de-DE")}</td>
                <td style={{ padding: 4 }}>
                  {t.active && (
                    <button type="button" onClick={() => deactivateToken(t.id, t.token)} disabled={busy}>
                      Deaktivieren
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <section>
        <h3>Top-Korrektur-Patterns</h3>
        <p className="muted" style={{ maxWidth: 720 }}>
          Was Birgit immer wieder korrigiert — sortiert nach Häufigkeit. Patterns mit Häufigkeit ≥3 werden automatisch jede Nacht in <code>rules.jsonl</code> übernommen.
          "Manuell übernehmen" zwingt den Worker, jetzt durchzulaufen.
        </p>

        <button type="button" onClick={promotePatterns} disabled={busy} style={{ marginBottom: 16 }}>
          Patterns jetzt manuell in Regeln übernehmen
        </button>

        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ borderBottom: "1px solid #ccc" }}>
              <th style={{ textAlign: "left", padding: 4 }}>Feld-Pfad</th>
              <th style={{ textAlign: "left", padding: 4 }}>OCR las</th>
              <th style={{ textAlign: "left", padding: 4 }}>Korrigiert zu</th>
              <th style={{ textAlign: "right", padding: 4 }}>Häufigkeit</th>
              <th style={{ textAlign: "left", padding: 4 }}>Letzter Vorkomnis</th>
            </tr>
          </thead>
          <tbody>
            {patterns.map((p, i) => (
              <tr key={i} style={{ borderBottom: "1px solid #eee" }}>
                <td style={{ padding: 4, fontFamily: "monospace", fontSize: 12 }}>{p.scope}</td>
                <td style={{ padding: 4, fontFamily: "monospace" }}>{p.match_value}</td>
                <td style={{ padding: 4, fontFamily: "monospace" }}>{p.replace_value}</td>
                <td style={{ padding: 4, textAlign: "right" }}>{p.occurrence_count}</td>
                <td style={{ padding: 4 }}>{new Date(p.last_seen).toLocaleDateString("de-DE")}</td>
              </tr>
            ))}
            {patterns.length === 0 && (
              <tr>
                <td colSpan={5} className="muted" style={{ padding: 12, textAlign: "center" }}>
                  Noch keine Korrektur-Patterns gefunden.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </section>
    </div>
  );
}
